import React, { useState, useContext } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { useSidenav } from "../context/SidenavContext";
import LanguageSelector from "./LanguageSelector";
import ThemeToggle from "./ThemeToggle";
import useMediaQuery from "@mui/material/useMediaQuery";
// import AddIcon from '@mui/icons-material/Add'; // Not directly used, can be removed
// import { keyframes } from '@mui/system'; // pulse animation not used, can be removed
import MenuIcon from '@mui/icons-material/Menu';
import ClickAwayListener from '@mui/material/ClickAwayListener';

// Material UI components
import {
  Avatar,
  Box,
  Button, // Added for new FAB location
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useTheme,
  alpha,
} from "@mui/material";

// Material UI icons
import DashboardIcon from "@mui/icons-material/Dashboard";
import EngineeringIcon from "@mui/icons-material/Engineering";
import DescriptionIcon from "@mui/icons-material/Description";
import NoteAltIcon from "@mui/icons-material/NoteAlt";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import LogoutIcon from "@mui/icons-material/Logout";
import PeopleIcon from "@mui/icons-material/People";
import InventoryIcon from "@mui/icons-material/Inventory";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
// import ChevronRightIcon from "@mui/icons-material/ChevronRight"; // Not used
import SettingsIcon from "@mui/icons-material/Settings";

const drawerWidth = 240;
const miniDrawerWidth = 65; // Adjusted to better fit icons

const Navigation = () => {
  const { currentUser, logout, isManager, isAdmin } = useContext(AuthContext);
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  // const isDark = theme.palette.mode === 'dark'; // theme.palette.mode is sufficient

  const [mobileOpen, setMobileOpen] = useState(false);
  const { collapsed, setCollapsed } = useSidenav(); // Sidenav context controls collapsed state
  const [anchorElUser, setAnchorElUser] = useState(null);

  // Consistent borderRadius from theme
  const componentBorderRadius = theme.shape.borderRadius;

  React.useEffect(() => {
    if (!isDesktop && collapsed) {
      // If on mobile and drawer is set to collapsed (e.g. by SidenavContext default), un-collapse it.
      // Mobile view uses temporary drawer, not collapsed state.
      setCollapsed(false);
    }
  }, [isDesktop, collapsed, setCollapsed]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleLogout = () => {
    handleCloseUserMenu();
    logout();
    navigate("/login");
  };

  const isMobile = !isDesktop;

  const handleClickAway = () => {
    // Only collapse on desktop if it's not already collapsed.
    // For mobile, this closes the temporary drawer.
    if (isDesktop && !collapsed) {
      // setCollapsed(true); // This might be too aggressive, let user control desktop collapse.
    }
    if (isMobile && mobileOpen) {
      setMobileOpen(false);
    }
  };

  const navigationItems = [
    {
      text: t("dashboard"),
      link: "/",
      icon: <DashboardIcon />,
    },
    {
      text: t("excavators"),
      link: "/excavators",
      icon: <EngineeringIcon />,
    },
    {
      text: t("reports"),
      link: "/reports",
      icon: <DescriptionIcon />,
    },
    {
      text: t("betriebstagebuch"),
      link: "/betriebstagebuch",
      icon: <NoteAltIcon />,
    },
    {
      text: t("inventory"),
      link: "/inventory",
      icon: <InventoryIcon />,
    },
  ];

  if (isManager || isAdmin) {
    navigationItems.push({
      text: t("users"),
      link: "/users",
      icon: <PeopleIcon />,
    });
  }

  const userMenuItems = [
    {
      text: t("profile"),
      link: "/profile",
      icon: <AccountCircleIcon fontSize="small" />,
    },
    {
      text: t("logout"),
      icon: <LogoutIcon fontSize="small" />,
      onClick: handleLogout,
    },
  ];

  const isActiveRoute = (path) => {
    if (path === "/" && location.pathname !== "/") {
      return false;
    }
    return location.pathname.startsWith(path);
  };

  // Section grouping
  const navSections = [
    {
      title: t("main"),
      items: [navigationItems[0], navigationItems[1], navigationItems[2], navigationItems[3], navigationItems[4]],
    },
    ((isManager || isAdmin) && navigationItems[5]) ? { // Ensure item exists before creating section
      title: t("management"),
      items: [navigationItems[5]],
    } : null,
  ].filter(Boolean); // Filter out null sections if user is not manager/admin

  const drawerPaperSx = {
    width: isDesktop && collapsed ? miniDrawerWidth : drawerWidth,
    boxSizing: 'border-box',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
    borderRight: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
  };

  const drawerContent = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        bgcolor: 'transparent',
        position: 'relative',
      }}
    >
      <Box> {/* Top part of the drawer: Logo, Toggle, Nav Items */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          height: 64,
          px: collapsed ? 0 : 2,
        }}>
          {!collapsed && (
            <Typography component={Link} to="/" variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary', textDecoration: 'none', whiteSpace: 'nowrap' }}>
              Fleet<span style={{color: theme.palette.primary.main}}>Pilot</span>
            </Typography>
          )}
          <Tooltip title={collapsed ? (t('openMenu') || 'Open menu') : (t('collapseMenu') || 'Collapse menu')}>
            <IconButton
              onClick={() => setCollapsed(!collapsed)}
              size="large"
              sx={{ color: 'text.secondary' }}
            >
              {collapsed ? <MenuIcon /> : <ChevronLeftIcon />}
            </IconButton>
          </Tooltip>
        </Box>
        <Divider />

        <Box sx={{ flexGrow: 1, px: collapsed ? 0.5 : 1, py:1, overflowY: 'auto', overflowX: 'hidden' }}>
          {navSections.map((section, idx) => (
            <Box key={section.title || `section-${idx}`} sx={{ mb: 1.5 }}>
              {!collapsed && section.title && (
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{
                    pl: 1.5,
                    mb: 0.5,
                    letterSpacing: 0.8,
                    fontSize: '0.7rem',
                    display: 'block'
                  }}
                >
                  {section.title}
                </Typography>
              )}
              <List disablePadding>
                {section.items.map((item) => (
                  <Tooltip
                    key={item.link}
                    title={collapsed ? item.text : ""}
                    placement="right"
                    arrow
                    disableHoverListener={!collapsed}
                  >
                    <ListItem disablePadding sx={{ display: "block", px: collapsed ? 0.5 : 1, py: 0.25 }}>
                      <ListItemButton
                        component={Link}
                        to={item.link}
                        selected={isActiveRoute(item.link)}
                        sx={{
                          minHeight: 44,
                          justifyContent: collapsed ? "center" : "flex-start",
                          px: collapsed ? 1.5 : 2,
                          borderRadius: componentBorderRadius,
                          mb: 0.5,
                          transition: theme.transitions.create(['background-color', 'color', 'box-shadow'], {
                              duration: theme.transitions.duration.short,
                          }),
                          '&.Mui-selected': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.12), // From theme MuiListItem selected
                            color: theme.palette.primary.main, // From theme MuiListItem selected
                            // boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}`, // Active indicator
                            '& .MuiListItemIcon-root': {
                              color: theme.palette.primary.main
                            },
                            '& .MuiListItemText-primary': {
                                fontWeight: 500,
                            }
                          },
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.06), // Adjusted hover
                            color: theme.palette.primary.main,
                            '& .MuiListItemIcon-root': {
                              color: theme.palette.primary.main
                            },
                          }
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 0,
                            mr: collapsed ? 0 : 1.5,
                            justifyContent: "center",
                            color: isActiveRoute(item.link) ? theme.palette.primary.main : theme.palette.text.secondary,
                            transition: theme.transitions.create('color', {duration: theme.transitions.duration.short}),
                          }}
                        >
                          {item.icon}
                        </ListItemIcon>
                        {!collapsed && (
                          <ListItemText
                            primary={item.text}
                            primaryTypographyProps={{
                              noWrap: true,
                              variant: 'body2',
                              fontWeight: isActiveRoute(item.link) ? 500 : 400
                            }}
                          />
                        )}
                      </ListItemButton>
                    </ListItem>
                  </Tooltip>
                ))}
              </List>
              {idx < navSections.length - 1 && !collapsed && <Divider sx={{ my: 1.5, mx: 2 }} />}
            </Box>
          ))}
        </Box>
      </Box>

      <Box> {/* Bottom part of the drawer */}
        {isDesktop && !collapsed && (
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1, borderTop: `1px solid ${theme.palette.divider}` }}>
            <Button
              variant="outlined" // Changed from contained for less emphasis
              color="primary"
              startIcon={<DescriptionIcon />}
              onClick={() => navigate("/reports/create")}
              fullWidth
              size="small"
            >
              {t("createReport")}
            </Button>
            <Button
              variant="outlined" // Changed from contained
              color="secondary"
              startIcon={<NoteAltIcon />}
              onClick={() => navigate("/betriebstagebuch/create")}
              fullWidth
              size="small"
            >
              {t("createBetriebstagebuch")}
            </Button>
          </Box>
        )}

        <Box sx={{ px: collapsed ? 0.5 : 2, py: 1, display: 'flex', justifyContent: collapsed ? 'center' : 'space-between', alignItems: 'center', gap: 1, borderTop: `1px solid ${theme.palette.divider}` }}>
          <LanguageSelector variant={collapsed ? "icon" : "full"} size="small" />
          <ThemeToggle size="small" />
        </Box>

        <Box sx={{
            p: collapsed ? 1 : 1.5,
            py: 1.5,
            borderTop: `1px solid ${theme.palette.divider}`,
            bgcolor: alpha(theme.palette.background.default, 0.5),
            display: 'flex',
            alignItems: 'center',
            gap: collapsed ? 0 : 1.5
          }}>
          <Tooltip title={currentUser?.name || t("user")}>
            <Avatar
              alt={currentUser?.name || t("user")}
              src={currentUser?.profileImage || ""}
              sx={{
                width: collapsed ? 32 : 36,
                height: collapsed ? 32 : 36,
                boxShadow: theme.shadows[1],
                border: `2px solid ${theme.palette.primary.main}`,
                cursor: 'pointer',
                ml: collapsed ? 'auto' : 0,
                mr: collapsed ? 'auto' : 0,
              }}
              onClick={() => navigate('/profile')}
            >
              {currentUser?.firstName?.charAt(0) || currentUser?.email?.charAt(0)?.toUpperCase() || "U"}
            </Avatar>
          </Tooltip>
          {!collapsed && (
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={500} noWrap color="text.primary">
                {currentUser?.name || currentUser?.email}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {currentUser?.role && t(currentUser.role)}
              </Typography>
            </Box>
          )}
          {!collapsed && (
            <Tooltip title={t("logout") || "Logout"}>
              <IconButton size="small" onClick={handleLogout} sx={{color: 'text.secondary'}}>
                <LogoutIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Box>
  );

  const showMobileOpenButton = isMobile && !mobileOpen;

  return (
    <>
      {showMobileOpenButton && (
        <Tooltip title={t('openMenu') || 'Open menu'}>
          <IconButton
            onClick={() => setMobileOpen(true)}
            sx={{
              position: 'fixed',
              top: 16,
              left: 16,
              zIndex: theme.zIndex.drawer + 1,
              bgcolor: theme.palette.background.paper,
              color: theme.palette.primary.main,
              boxShadow: theme.shadows[3],
              border: `1px solid ${theme.palette.divider}`,
              '&:hover': { bgcolor: alpha(theme.palette.background.paper, 0.85) },
              display: { xs: 'inline-flex', md: 'none' }
            }}
            size="large"
          >
            <MenuIcon />
          </IconButton>
        </Tooltip>
      )}
      <ClickAwayListener
        onClickAway={isMobile ? handleClickAway : () => {}}
        mouseEvent="onMouseDown"
        touchEvent="onTouchStart" // Added touch event for better mobile experience
      >
        <Drawer
          anchor="left"
          variant={isDesktop ? "persistent" : "temporary"}
          open={isDesktop ? true : mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          PaperProps={{component: 'aside'}} // Semantic element for accessibility
          sx={{
            width: isDesktop && collapsed ? miniDrawerWidth : drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': drawerPaperSx,
            zIndex: theme.zIndex.drawer,
          }}
        >
          {drawerContent}
        </Drawer>
      </ClickAwayListener>

      <Menu
        id="user-menu"
        anchorEl={anchorElUser}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        keepMounted
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        open={Boolean(anchorElUser)}
        onClose={handleCloseUserMenu}
        PaperProps={{
            elevation: 0,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.15))', // Softer shadow
              mt: 1.5,
              minWidth: 200,
              borderRadius: componentBorderRadius,
              border: `1px solid ${theme.palette.divider}`,
              '& .MuiAvatar-root': {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              '&:before': { // Arrow pointing to the anchor
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
                borderLeft: `1px solid ${theme.palette.divider}`,
                borderTop: `1px solid ${theme.palette.divider}`,
              },
            },
          }}
      >
        {currentUser && (
          <Box sx={{ p: 2, pb: 1, borderBottom: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="subtitle2" fontWeight="bold" noWrap>
              {currentUser.name || currentUser.email}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {currentUser.email}
            </Typography>
          </Box>
        )}
        {/* <Divider sx={{ mb: 1 }} /> */}
        <MenuItem
          disabled
          sx={{ fontWeight: 'bold', opacity: '0.7 !important', fontSize: "0.8rem", color: 'text.secondary', mt:1, textTransform: 'uppercase' }}
        >
          {t("account")}
        </MenuItem>
        {userMenuItems.map((item) => (
          <MenuItem
            key={item.text}
            component={item.link ? Link : "li"} // Use li for button items for semantics
            to={item.link}
            onClick={item.onClick ? () => {item.onClick(); handleCloseUserMenu();} : handleCloseUserMenu}
            sx={{ py:1, px:2 }}
          >
            <ListItemIcon sx={{ minWidth: 32, color: 'text.secondary' }}>{item.icon}</ListItemIcon>
            <ListItemText primaryTypographyProps={{variant: 'body2'}}>{item.text}</ListItemText>
          </MenuItem>
        ))}
        <Divider sx={{ my: 1 }} />
        <MenuItem
          disabled
          sx={{ fontWeight: 'bold', opacity: '0.7 !important', fontSize: "0.8rem", color: 'text.secondary', textTransform: 'uppercase' }}
        >
          {t("settings")}
        </MenuItem>
        <MenuItem
          onClick={() => { navigate('/profile'); handleCloseUserMenu(); }} // Example: navigate to profile for settings
          sx={{ py:1, px:2 }}
        >
          <ListItemIcon sx={{ minWidth: 32, color: 'text.secondary' }}>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{variant: 'body2'}}>{t("profileSettings")}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export default Navigation;
